import { useContext, useMemo } from 'react';

import { bind } from 'bind-event-listener';
import { di } from 'react-magnetic-di';
import { fetchQuery, graphql, ReactRelayContext, type Environment } from 'react-relay';

import {
	MentionNodeDataProvider,
	type FetchMentionNodeData,
} from '@atlaskit/editor-plugin-mentions/mention-node-data-provider';
import type { MentionNodeData, MentionProvider } from '@atlaskit/mention/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { RovoAgentAri } from '@atlassian/ari';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import type { useRovoMentionNodeDataProviderQuery } from './__generated__/useRovoMentionNodeDataProviderQuery.graphql';

type MentionAvatarAgent = {
	id: string;
	avatarUrl: string | null;
	identityAccountId?: string;
};

type MentionAvatarAgentSource = {
	getAllAgents: (signal?: AbortSignal) => Promise<readonly MentionAvatarAgent[]>;
};

type UseRovoMentionNodeDataProviderOptions = {
	mentionResource: Promise<MentionProvider>;
	agentSource?: MentionAvatarAgentSource | null;
};

export type RovoMentionNodeDataProvider = MentionNodeDataProvider;

const fetchMentionDataFromRelay = async (
	environment: Environment | null,
	mentionIds: string[],
	signal: AbortSignal,
): Promise<Record<string, MentionNodeData>> => {
	if (!environment || mentionIds.length === 0 || signal.aborted) {
		return {};
	}

	try {
		const response = await new Promise<
			useRovoMentionNodeDataProviderQuery['response'] | null | undefined
		>((resolve, reject) => {
			let unbindAbortListener: (() => void) | null = null;
			let subscription: { unsubscribe: () => void } | null = null;
			let isSettled = false;
			const settle = (
				value: useRovoMentionNodeDataProviderQuery['response'] | null | undefined,
			) => {
				if (isSettled) {
					return;
				}
				isSettled = true;
				unbindAbortListener?.();
				subscription?.unsubscribe();
				resolve(value);
			};
			const fail = (error: Error) => {
				if (isSettled) {
					return;
				}
				isSettled = true;
				unbindAbortListener?.();
				subscription?.unsubscribe();
				reject(error);
			};

			unbindAbortListener = bind(signal, {
				type: 'abort',
				listener: () => settle(null),
				options: { once: true },
			});
			subscription = fetchQuery<useRovoMentionNodeDataProviderQuery>(
				environment,
				graphql`
					query useRovoMentionNodeDataProviderQuery($mentionIds: [ID!]!) {
						users(accountIds: $mentionIds) {
							accountId
							picture
							... on AppUser {
								appType
							}
						}
						agentStudio_agentsByIds(ids: $mentionIds) @optIn(to: "AgentStudio") {
							... on AgentStudioAssistant {
								id
								identityAccount {
									id
									picture
								}
							}
						}
					}
				`,
				{ mentionIds },
				{ fetchPolicy: 'store-or-network' },
			).subscribe({ next: settle, error: fail, complete: () => settle(null) });
			if (isSettled) {
				subscription.unsubscribe();
			}
		});

		const mentionIdSet = new Set(mentionIds);
		const dataByMentionId: Record<string, MentionNodeData> = {};

		(response?.users ?? []).forEach((user) => {
			if (user.picture && mentionIdSet.has(user.accountId)) {
				dataByMentionId[user.accountId] = {
					avatarUrl: user.picture,
					appType: user.appType,
				};
			}
		});
		(response?.agentStudio_agentsByIds ?? []).forEach((agent) => {
			const avatarUrl = agent?.identityAccount?.picture;
			if (!agent?.id || !avatarUrl) {
				return;
			}

			let studioAgentId = agent.id;
			try {
				studioAgentId = RovoAgentAri.parse(agent.id).agentId;
			} catch {
				// Some callers and test environments already expose the Studio UUID.
			}
			const identityAccountId = normalizeAccountIdFromIdentityAri(agent.identityAccount?.id);
			[studioAgentId, identityAccountId].forEach((id) => {
				if (typeof id === 'string' && mentionIdSet.has(id)) {
					dataByMentionId[id] = { avatarUrl, appType: 'agent' };
				}
			});
		});

		return dataByMentionId;
	} catch (error) {
		if (signal.aborted) {
			return {};
		}
		throw error;
	}
};

const fetchAgentsFromSource = async (
	agentSource: MentionAvatarAgentSource | null | undefined,
	mentionIds: string[],
	signal: AbortSignal,
): Promise<Record<string, MentionNodeData>> => {
	if (!agentSource || mentionIds.length === 0 || signal.aborted) {
		return {};
	}

	try {
		const mentionIdSet = new Set(mentionIds);
		const agents = await agentSource.getAllAgents(signal);
		if (signal.aborted) {
			return {};
		}
		const dataByMentionId: Record<string, MentionNodeData> = {};

		agents.forEach((agent) => {
			if (!agent.avatarUrl) {
				return;
			}
			const avatarUrl = agent.avatarUrl;

			const identityAccountId = normalizeAccountIdFromIdentityAri(agent.identityAccountId);
			[agent.id, identityAccountId].forEach((id) => {
				if (typeof id === 'string' && mentionIdSet.has(id)) {
					dataByMentionId[id] = { avatarUrl, appType: 'agent' };
				}
			});
		});

		return dataByMentionId;
	} catch (error) {
		if (signal.aborted) {
			return {};
		}
		throw error;
	}
};

const createFetchMentionNodeData = (
	environment: Environment | null,
	agentSource: MentionAvatarAgentSource | null | undefined,
): FetchMentionNodeData => {
	return async (mentionIds, signal = new AbortController().signal) => {
		let relayData: Record<string, MentionNodeData> = {};
		let relayError: Error | undefined;
		try {
			relayData = await fetchMentionDataFromRelay(environment, mentionIds, signal);
		} catch (error) {
			relayError = error instanceof Error ? error : new Error(String(error));
		}
		const unresolvedMentionIds = mentionIds.filter((id) => relayData[id] === undefined);
		let agentData: Record<string, MentionNodeData> = {};
		let agentSourceError: Error | undefined;
		try {
			agentData = await fetchAgentsFromSource(agentSource, unresolvedMentionIds, signal);
		} catch (error) {
			agentSourceError = error instanceof Error ? error : new Error(String(error));
		}
		const dataByMentionId = { ...relayData, ...agentData };
		const hasUnresolvedMentions = mentionIds.some((id) => dataByMentionId[id] === undefined);
		const fetchError = relayError ?? agentSourceError;
		if (!signal.aborted && hasUnresolvedMentions && fetchError) {
			throw new Error(`Failed to load Rovo mention avatar data: ${fetchError.message}`);
		}

		return dataByMentionId;
	};
};

/**
 * Creates one mention-avatar provider per mounted Rovo editor or renderer.
 * Picker results populate a synchronous cache; cold ADF falls back to Relay,
 * then to the existing agent source for legacy Studio-id mention nodes.
 * The Rovo experiment gates all provider work; the existing Platform experiment
 * continues to gate avatar rendering and exposure inside the mention component.
 */
export const useRovoMentionNodeDataProvider = ({
	mentionResource,
	agentSource,
}: UseRovoMentionNodeDataProviderOptions): RovoMentionNodeDataProvider | undefined => {
	di(fetchQuery);
	const environment = useContext(ReactRelayContext)?.environment ?? null;
	const mentionNodeDataProvider = useMemo(() => {
		if (!isExperimentEnabled('platform_editor_rovo_editor_mention_node_avatar')) {
			return undefined;
		}

		return new MentionNodeDataProvider(createFetchMentionNodeData(environment, agentSource));
	}, [agentSource, environment]);
	return mentionNodeDataProvider;
};
